import { FC, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTestingApi } from '../../../leopart-update/hooks/useTestingApi';
import dayjs from 'dayjs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IWeightHistorySession } from '../../../models/programming';

const CATEGORY_LABELS: Record<string, string> = {
  forza: 'Forza', salto: 'Salto', velocita: 'Velocità', resistenza: 'Resistenza',
  mobilita: 'Mobilità', composizione: 'Composizione', rtp_rts: 'RTP/RTS', altro: 'Altro',
};

interface ExerciseSummary {
  exerciseId: string;
  exerciseName: string | { it?: string; en?: string } | null;
  lastWeight: number;
  maxWeight: number;
  trend: number;
  totalSessions?: number;
  executionDays?: number;
}

function resolveExerciseName(ex: ExerciseSummary): string {
  const raw = ex.exerciseName;
  if (!raw) return ex.exerciseId;
  if (typeof raw === 'string') return raw || ex.exerciseId;
  return raw.it || raw.en || ex.exerciseId;
}

interface AthleteExerciseHistoryProps {
  athleteId: string;
}

export const AthleteExerciseHistory: FC<AthleteExerciseHistoryProps> = ({ athleteId }) => {
  const { getWeightHistorySummary, getWeightHistory } = useEndpoints();
  const [exercises, setExercises] = useState<ExerciseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<{ date: string; value: number }[]>([]);
  const [sessions, setSessions] = useState<IWeightHistorySession[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedCat, setSelectedCat] = useState<string | null>(null);

  // Step categoria (spec sez.9.1): mappa exerciseId→categoria dal catalogo unificato
  const { catalog } = useTestingApi();
  const catByExId = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of catalog) if (t.exerciseId) m.set(t.exerciseId, t.category);
    return m;
  }, [catalog]);
  const exCategory = (exId: string) => catByExId.get(exId) ?? 'altro';
  const availableCats = useMemo(() => {
    const s = new Set<string>();
    for (const ex of exercises) s.add(exCategory(ex.exerciseId));
    return Array.from(s).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercises, catByExId]);
  const filteredExercises = useMemo(
    () => (selectedCat ? exercises.filter((ex) => exCategory(ex.exerciseId) === selectedCat) : exercises),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exercises, selectedCat, catByExId],
  );

  useEffect(() => {
    if (!athleteId) return;
    setLoading(true);
    getWeightHistorySummary(athleteId)
      .then((res) => {
        const data = res.data;
        if (Array.isArray(data)) {
          setExercises(data);
        } else if (data?.exercises) {
          setExercises(data.exercises);
        } else {
          setExercises([]);
        }
      })
      .catch((err) => {
        console.error('Error loading exercise summary:', err);
        setExercises([]);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  const handleSelectExercise = async (exerciseId: string) => {
    setSelectedExercise(exerciseId);
    setHistoryLoading(true);
    try {
      const res = await getWeightHistory(athleteId, exerciseId, { limit: 100 });
      setHistoryData(res.data?.history || []);
      setSessions(res.data?.sessions || []);
    } catch (err) {
      console.error('Error loading exercise history:', err);
      setHistoryData([]);
      setSessions([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Caricamento storico esercizi...</div>;
  }

  const totalExecutions = exercises.reduce(
    (s, e) => s + (e.totalSessions ?? e.executionDays ?? 0),
    0,
  );

  return (
    <div>
      {/* Riepilogo coach-first */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            background: 'var(--c-app-bg)',
            border: '1px solid #e7e7e7',
            borderRadius: 12,
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontFamily: "'Satoshi:Medium', sans-serif",
              fontWeight: 700,
              color: 'var(--c-text-1)',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Esercizi tracciati
          </div>
          <div
            style={{
              fontSize: 22,
              fontFamily: "'Satoshi:Medium', sans-serif",
              fontWeight: 700,
              color: 'var(--c-text-0)',
              letterSpacing: '-0.025em',
            }}
          >
            {exercises.length}
          </div>
        </div>
        <div
          style={{
            background: 'var(--c-app-bg)',
            border: '1px solid #e7e7e7',
            borderRadius: 12,
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontFamily: "'Satoshi:Medium', sans-serif",
              fontWeight: 700,
              color: 'var(--c-text-1)',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Esecuzioni totali
          </div>
          <div
            style={{
              fontSize: 22,
              fontFamily: "'Satoshi:Medium', sans-serif",
              fontWeight: 700,
              color: 'var(--c-text-0)',
              letterSpacing: '-0.025em',
            }}
          >
            {totalExecutions}
          </div>
        </div>
        <div
          style={{
            background: 'var(--c-app-bg)',
            border: '1px solid #e7e7e7',
            borderRadius: 12,
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontFamily: "'Satoshi:Medium', sans-serif",
              fontWeight: 700,
              color: 'var(--c-text-1)',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Origine
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "'Satoshi', sans-serif",
                background: '#e0f2fe',
                color: '#075985',
                padding: '3px 8px',
                borderRadius: 6,
              }}
            >
              🏋 Palestra
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "'Satoshi', sans-serif",
                background: '#dcfce7',
                color: '#166534',
                padding: '3px 8px',
                borderRadius: 6,
              }}
            >
              ⚽ Campo
            </span>
          </div>
        </div>
      </div>

      {/* Step categoria (spec sez.9.1): filtra gli esercizi per categoria */}
      {availableCats.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {[null, ...availableCats].map((cat) => {
            const active = selectedCat === cat;
            return (
              <button
                key={cat ?? 'all'}
                onClick={() => setSelectedCat(cat)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: '1px solid var(--c-card-border)',
                  background: active ? 'var(--c-navy-1)' : 'transparent',
                  color: active ? '#fff' : 'var(--c-text-1)',
                }}
              >
                {cat ? CATEGORY_LABELS[cat] ?? cat : 'Tutte'}
              </button>
            );
          })}
        </div>
      )}

      {/* Exercise table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 24 }}>
        <thead>
          <tr>
            <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Esercizio</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Ultimo carico</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Max carico</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Esecuzioni</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Trend</th>
          </tr>
        </thead>
        <tbody>
          {filteredExercises.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#718096' }}>
                Nessun dato disponibile.
              </td>
            </tr>
          )}
          {filteredExercises.map((ex) => (
            <tr
              key={ex.exerciseId}
              onClick={() => handleSelectExercise(ex.exerciseId)}
              style={{
                cursor: 'pointer',
                backgroundColor: selectedExercise === ex.exerciseId ? '#EBF8FF' : 'transparent',
              }}
            >
              <td style={{ padding: '8px 12px', borderBottom: '1px solid #edf2f7', fontWeight: 500 }}>
                {/* Link alla vista per-esercizio unificata (3 sezioni: Programmazione+Testing+Profiling, spec sez.9) */}
                <Link
                  to={`/dashboard/users/${athleteId}/exercises/${ex.exerciseId}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: 'var(--c-text-0)', textDecoration: 'none', borderBottom: '1px dotted var(--c-text-3)' }}
                  title="Apri storico completo (allenamento + testing + profiling)"
                >
                  {resolveExerciseName(ex)}
                </Link>
              </td>
              <td style={{ padding: '8px 12px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                {ex.lastWeight ? `${ex.lastWeight} kg` : '—'}
              </td>
              <td style={{ padding: '8px 12px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                {ex.maxWeight ? `${ex.maxWeight} kg` : '—'}
              </td>
              <td style={{ padding: '8px 12px', borderBottom: '1px solid #edf2f7', textAlign: 'right', color: '#4A5568' }}>
                {ex.totalSessions != null ? ex.totalSessions : (ex.executionDays != null ? ex.executionDays : '—')}
              </td>
              <td style={{ padding: '8px 12px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>
                <span style={{ color: ex.trend >= 0 ? '#38A169' : '#E53E3E' }}>
                  {ex.trend >= 0 ? '+' : ''}{ex.trend?.toFixed(1) || '0'}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Detail chart */}
      {selectedExercise && (
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Progressione carico</h4>
          {historyLoading ? (
            <div style={{ padding: 16, textAlign: 'center' }}>Caricamento...</div>
          ) : historyData.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#718096' }}>Nessun dato storico per questo esercizio.</div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={historyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#3182CE" name="Carico (kg)" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}

          {/* Sessioni per data — set-by-set */}
          {!historyLoading && sessions.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                Esecuzioni per data
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sessions.map((s) => (
                  <SessionCard key={s.logId} session={s} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SessionCard: FC<{ session: IWeightHistorySession }> = ({ session }) => {
  const dateLabel = dayjs(session.date).format('DD MMM YYYY');
  const hasMV = session.sets.some((st) => st.meanVelocity != null);
  const hasLat = session.sets.some((st) => st.laterality != null);

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        background: 'var(--c-app-bg)',
        padding: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{dateLabel}</span>
        {session.exerciseStatus && (
          <span
            style={{
              fontSize: 10,
              padding: '2px 6px',
              borderRadius: 4,
              background: session.exerciseStatus === 'completed' ? '#dcfce7' : '#fef3c7',
              color: session.exerciseStatus === 'completed' ? '#166534' : '#92400e',
              fontWeight: 600,
            }}
          >
            {session.exerciseStatus}
          </span>
        )}
        {session.blockType && (
          <span style={{ fontSize: 11, color: '#718096' }}>· {session.blockType}</span>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ color: '#4A5568' }}>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>Set</th>
            <th style={{ textAlign: 'right', padding: '4px 8px' }}>Reps</th>
            <th style={{ textAlign: 'right', padding: '4px 8px' }}>Carico</th>
            <th style={{ textAlign: 'right', padding: '4px 8px' }}>RPE</th>
            {hasMV && (
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>Vel media</th>
            )}
            {hasLat && (
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>Lato</th>
            )}
          </tr>
        </thead>
        <tbody>
          {session.sets.map((st) => (
            <tr key={st.setNumber} style={{ borderTop: '1px solid #edf2f7' }}>
              <td style={{ padding: '6px 8px' }}>{st.setNumber}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                {st.reps != null ? st.reps : '—'}
              </td>
              <td
                style={{
                  padding: '6px 8px',
                  textAlign: 'right',
                  color: st.isPrescribed ? '#A0AEC0' : 'inherit',
                  fontStyle: st.isPrescribed ? 'italic' : 'normal',
                }}
                title={st.isPrescribed ? 'Valore prescritto (non compilato dall\'atleta)' : undefined}
              >
                {st.load != null ? `${st.load} ${st.loadUnit}` : '—'}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                {st.rpe != null ? st.rpe : '—'}
              </td>
              {hasMV && (
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {st.meanVelocity != null ? `${st.meanVelocity.toFixed(2)} m/s` : '—'}
                </td>
              )}
              {hasLat && (
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {st.laterality ?? '—'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {session.exerciseNotes && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#4A5568', fontStyle: 'italic' }}>
          Note: {session.exerciseNotes}
        </div>
      )}
    </div>
  );
};

export default AthleteExerciseHistory;
